import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { customerService, promoService } from "@/lib/database";
import {
  Customer,
  PromoCode,
  ShipmentDiscount,
  PromoCodeUsage,
} from "@/lib/supabase";
import {
  ArrowLeft,
  Plus,
  Eye,
  Save,
  ChevronDownIcon,
  UserPlus,
  UserMinus,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function PromoCodeDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [promoCode, setPromoCode] = useState<PromoCode | null>(null);
  const [customerUsages, setCustomerUsages] = useState<PromoCodeUsage[]>([]);
  const [shipmentDiscounts, setShipmentDiscounts] = useState<
    ShipmentDiscount[]
  >([]);
  const [_, setLoading] = useState(false);

  const [editableDescription, setEditableDescription] = useState("");
  const [editableStackable, setEditableStackable] = useState(false);

  const [editableStartDate, setEditableStartDate] = useState<
    Date | undefined
  >();
  const [editableStartTime, setEditableStartTime] = useState("");
  const [editableEndDate, setEditableEndDate] = useState<Date | undefined>();
  const [editableEndTime, setEditableEndTime] = useState("");
  const [openStartDatePicker, setOpenStartDatePicker] = useState(false);
  const [openEndDatePicker, setOpenEndDatePicker] = useState(false);
  const [hasValidityPeriod, setHasValidityPeriod] = useState(false);

  const [editableUsageLimitPerCustomer, setEditableUsageLimitPerCustomer] =
    useState<number | undefined>();
  const [editableTotalUsageLimit, setEditableTotalUsageLimit] = useState<
    number | undefined
  >();
  const [hasUsageLimits, setHasUsageLimits] = useState(false);

  const loadPromoCode = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await promoService.getById(id);

      setPromoCode(data);

      setCustomerUsages(data.usages || []);
      setShipmentDiscounts(data.shipment_discounts || []);

      setEditableDescription(data.description || "");
      setEditableStackable(!!data.is_stackable);

      setHasUsageLimits(
        !!(data.usage_limit_per_customer || data.total_usage_limit)
      );
      setEditableUsageLimitPerCustomer(
        data.usage_limit_per_customer ?? undefined
      );
      setEditableTotalUsageLimit(data.total_usage_limit ?? undefined);

      setHasValidityPeriod(!!(data.start_date && data.end_date));
      setEditableStartDate(
        data.start_date ? new Date(data.start_date) : undefined
      );
      setEditableStartTime(
        data.start_date
          ? new Date(data.start_date).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "00:00:00"
      );

      setEditableEndDate(data.end_date ? new Date(data.end_date) : undefined);
      setEditableEndTime(
        data.end_date
          ? new Date(data.end_date).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "00:00:00"
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const savePromoCodeInfo = async () => {
    if (!promoCode) return;

    try {
      setLoading(true);

      const startDateTime =
        hasValidityPeriod && editableStartDate
          ? new Date(
              `${
                editableStartDate.toISOString().split("T")[0]
              }T${editableStartTime}`
            )
          : null;

      const endDateTime =
        hasValidityPeriod && editableEndDate
          ? new Date(
              `${
                editableEndDate.toISOString().split("T")[0]
              }T${editableEndTime}`
            )
          : null;

      if (hasValidityPeriod) {
        if (!startDateTime || !endDateTime)
          throw new Error("Please select both start and end dates.");
      }

      const updated = await promoService.update(promoCode?.id, {
        description: editableDescription,
        start_date: startDateTime?.toISOString() || null,
        end_date: endDateTime?.toISOString() || null,
        is_stackable: editableStackable,
        usage_limit_per_customer:
          hasUsageLimits && editableUsageLimitPerCustomer
            ? editableUsageLimitPerCustomer
            : null,
        total_usage_limit:
          hasUsageLimits && editableTotalUsageLimit
            ? editableTotalUsageLimit
            : null,
      });

      setPromoCode(updated);
      toast.success("Promo code info updated.", {
        position: "top-center",
      });
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Unexpected error.", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPromoCode();
  }, [id]);

  return (
    <div className="space-y-6">
      <div className="flex space-x-4 items-center">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {promoCode?.code}
          </h1>
          <p className="text-gray-600">
            {promoCode?.description || "No description"}
          </p>
        </div>
      </div>

      {/* General Info */}
      <Card>
        <CardHeader>
          <CardTitle>Promo Code Info</CardTitle>
          <CardDescription>General details of the promo code</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Description</Label>
            <Input
              value={editableDescription}
              onChange={(e) => setEditableDescription(e.target.value)}
            />
          </div>

          <div className="flex space-x-6">
            <div>
              <Label>Type</Label>
              <p className="text-sm text-muted-foreground">
                {promoCode?.promo_type}
              </p>
            </div>

            <div>
              <Label>Amount</Label>
              <p className="text-sm text-muted-foreground">
                {promoCode?.amount}
              </p>
            </div>
          </div>

          <div className="space-x-2 items-center flex">
            <Checkbox
              checked={hasUsageLimits}
              id="usage-limits"
              onCheckedChange={(checked: boolean) => {
                setHasUsageLimits(checked);

                if (!checked) {
                  setEditableUsageLimitPerCustomer(
                    promoCode?.usage_limit_per_customer
                      ? promoCode?.usage_limit_per_customer
                      : undefined
                  );
                  setEditableTotalUsageLimit(
                    promoCode?.total_usage_limit
                      ? promoCode?.total_usage_limit
                      : undefined
                  );
                }
              }}
            />
            <Label htmlFor="usage-limits">Usage Limits</Label>
          </div>

          {hasUsageLimits && (
            <div className="flex space-x-4">
              <div className="flex flex-col space-y-2">
                <Label>Usage Limit Per Customer</Label>

                <Input
                  type="number"
                  className="w-full"
                  inputMode="decimal"
                  value={editableUsageLimitPerCustomer ?? ""}
                  onChange={(e) =>
                    setEditableUsageLimitPerCustomer(
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                />
              </div>

              <div className="flex flex-col space-y-2">
                <Label>Total Usage Cap</Label>

                <Input
                  type="number"
                  className="w-full"
                  inputMode="decimal"
                  value={editableTotalUsageLimit ?? ""}
                  onChange={(e) =>
                    setEditableTotalUsageLimit(
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                />
              </div>
            </div>
          )}

          <div className="space-x-2 items-center flex">
            <Checkbox
              checked={hasValidityPeriod}
              id="validity-period"
              onCheckedChange={(checked: boolean) => {
                setHasValidityPeriod(checked);

                if (!checked) {
                  setEditableStartDate(
                    promoCode?.start_date
                      ? new Date(promoCode?.start_date)
                      : undefined
                  );
                  setEditableEndDate(
                    promoCode?.end_date
                      ? new Date(promoCode?.end_date)
                      : undefined
                  );
                }
              }}
            />
            <Label htmlFor="validity-period">Validity Period</Label>
          </div>

          {hasValidityPeriod && (
            <div className="flex flex-col gap-4 md:flex-row flex-wrap">
              <div className="flex gap-4 justify-between">
                <div className="flex flex-col gap-3">
                  <Label className="px-1">Start Date</Label>
                  <Popover
                    open={openStartDatePicker}
                    onOpenChange={setOpenStartDatePicker}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-32 justify-between font-normal"
                      >
                        {editableStartDate
                          ? editableStartDate.toLocaleDateString()
                          : "Select date"}
                        <ChevronDownIcon />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editableStartDate}
                        captionLayout="dropdown"
                        onSelect={(date) => {
                          setEditableStartDate(date);
                          setOpenStartDatePicker(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex flex-col gap-3">
                  <Label className="px-1">Start Time</Label>
                  <Input
                    type="time"
                    step="1"
                    value={editableStartTime}
                    onChange={(e) => setEditableStartTime(e.target.value)}
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
                  />
                </div>
              </div>

              <div className="flex gap-4 justify-between">
                <div className="flex flex-col gap-3">
                  <Label className="px-1">End Date</Label>
                  <Popover
                    open={openEndDatePicker}
                    onOpenChange={setOpenEndDatePicker}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-32 justify-between font-normal"
                      >
                        {editableEndDate
                          ? editableEndDate.toLocaleDateString()
                          : "Select date"}
                        <ChevronDownIcon />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editableEndDate}
                        captionLayout="dropdown"
                        onSelect={(date) => {
                          setEditableEndDate(date);
                          setOpenEndDatePicker(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex flex-col gap-3">
                  <Label className="px-1">End Time</Label>
                  <Input
                    type="time"
                    step="1"
                    value={editableEndTime}
                    onChange={(e) => setEditableEndTime(e.target.value)}
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Switch
              checked={editableStackable}
              onCheckedChange={setEditableStackable}
            />
            <Label>Stackable</Label>
          </div>

          <Button onClick={savePromoCodeInfo}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* Assigned Customers */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Usage</CardTitle>
          <CardDescription>
            Customers who have used this promo code
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!!!customerUsages.length && (
            <p className="text-sm text-muted-foreground flex justify-center">
              This promo code hasn't been used yet.
            </p>
          )}

          {customerUsages.map((usage) => (
            <div
              key={usage.id}
              className="space-y-6 md:space-y-0 flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white"
            >
              <div className="flex flex-col items-center md:items-start">
                <div className="font-medium">
                  {usage.customer?.first_name} {usage.customer?.last_name}
                </div>

                <div className="text-sm text-gray-600">
                  {usage.customer?.email}
                </div>
              </div>

              <div className="flex space-y-6 md:space-y-0 md:space-x-6 flex-col md:flex-row">
                <div className="flex space-x-6 justify-center">
                  <div className="text-center flex flex-col">
                    <Label>Usage Count</Label>
                    <p className="text-sm text-muted-foreground">
                      {usage.usage_count}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Shipments using this promo code */}
      <Card>
        <CardHeader>
          <CardTitle>Shipments</CardTitle>
          <CardDescription>Shipments that used this promo code</CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {shipmentDiscounts.length === 0 && (
            <p className="text-sm text-muted-foreground flex justify-center">
              This promo code hasn't been used yet.
            </p>
          )}

          {shipmentDiscounts.map((sd) => (
            <div
              key={sd.id}
              className="flex flex-col space-y-6 md:space-y-0 md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white"
            >
              <div className="flex flex-col items-center md:items-start">
                <p className="font-medium">{sd.shipment?.tracking_number}</p>

                {!!sd.shipment?.created_at && (
                  <p className="text-sm text-gray-600">
                    {new Date(sd.shipment?.created_at).toLocaleDateString()}
                  </p>
                )}

                <p className="text-sm">
                  {sd.shipment?.customer?.first_name}{" "}
                  {sd.shipment?.customer?.last_name}
                </p>

                <p className="text-sm text-gray-600">
                  {sd.shipment?.route === "us_to_ht"
                    ? "USA to Haiti"
                    : "Haiti to USA"}
                </p>
              </div>

              <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 items-center space-x-0 md:space-x-4 mt-2 md:mt-0">
                <div className="flex flex-col items-center space-y-1">
                  <Label>Total Cost</Label>

                  <span className="text-sm text-muted-foreground">
                    ${sd.shipment?.total_cost}
                  </span>
                </div>

                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/admin/shipments/${sd.shipment?.tracking_number}`)
                  }
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
